/**
 * SEO Utils — Factories reutilizáveis para schema.org e meta tags
 *
 * Regras:
 * 1. Todas as datas usam BUILD_DATE (injetada no build).
 * 2. Schemas são plain objects — o consumidor faz JSON.stringify.
 * 3. Nenhuma dependência de React aqui.
 */

import { defaultAuthor, type Author } from '@/data/author';

export const SITE = {
  name: 'Vem Pra Ilha do Mel',
  url: 'https://vemprailhadomel.com.br',
  logo: 'https://vemprailhadomel.com.br/logo.png',
  twitter: '@vemprailhadomel',
  instagram: 'https://instagram.com/vemprailhadomel',
  locale: 'pt_BR',
  description:
    'Guia turístico independente da Ilha do Mel, litoral do Paraná: praias, trilhas, pousadas, restaurantes, passeios, marés e dicas práticas.',
} as const;

/* ─── @id estáveis para o grafo JSON-LD (linkagem entre schemas) ─── */
export const ORG_ID = `${SITE.url}/#organization`;
export const WEBSITE_ID = `${SITE.url}/#website`;
export const LOGO_ID = `${SITE.url}/#logo`;
export const personId = (slug: string) => `${SITE.url}/#person-${slug}`;

/**
 * Data do build. Em dev usa a data atual; em produção pode ser
 * sobrescrita via VITE_BUILD_DATE no momento do deploy.
 */
export const BUILD_DATE = import.meta.env.VITE_BUILD_DATE ?? new Date().toISOString().split('T')[0];

/* ─── Schema Builders ─── */

export function createOrganizationSchema() {
  return {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: SITE.name,
    url: SITE.url,
    description: SITE.description,
    logo: {
      '@type': 'ImageObject',
      '@id': LOGO_ID,
      url: SITE.logo,
      caption: SITE.name,
    },
    image: { '@id': LOGO_ID },
    sameAs: [SITE.instagram],
  };
}

/** WebSite — nó raiz do grafo, publicado pela Organization. */
export function createWebSiteSchema(lang: string = 'pt-BR') {
  return {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: SITE.url,
    name: SITE.name,
    description: SITE.description,
    publisher: { '@id': ORG_ID },
    inLanguage: lang,
  };
}

/** Person — o autor dos conteúdos (E-E-A-T), vinculado à Organization. */
export function createPersonSchema(author: Author = defaultAuthor) {
  return {
    '@type': 'Person',
    '@id': personId(author.slug),
    name: author.name,
    jobTitle: author.jobTitle,
    description: author.bio,
    url: author.profileUrl ? `${SITE.url}${author.profileUrl}` : SITE.url,
    worksFor: { '@id': ORG_ID },
    knowsAbout: author.knowsAbout,
    ...(author.photo && {
      image: {
        '@type': 'ImageObject',
        url: author.photo.startsWith('http') ? author.photo : `${SITE.url}${author.photo}`,
        caption: author.name,
      },
    }),
    ...(author.sameAs?.length && { sameAs: author.sameAs }),
  };
}

/**
 * Núcleo do grafo reutilizável: Organization + WebSite + Person.
 * Esses três nós são os mesmos em todas as páginas (linkados por @id).
 */
export function createCoreGraph(lang: string = 'pt-BR', author: Author = defaultAuthor) {
  return [createOrganizationSchema(), createWebSiteSchema(lang), createPersonSchema(author)];
}

/**
 * WebPage — a página em si, que amarra o grafo:
 * autor (Person), editora (Organization), assunto (a entidade) e breadcrumb.
 */
export function createWebPageSchema(
  opts: {
    url: string;
    name: string;
    description: string;
    image?: string;
    datePublished?: string;
    dateModified?: string;
    breadcrumbId?: string;
    aboutId?: string;
    authorSlug?: string;
  },
  lang: string = 'pt-BR'
) {
  return {
    '@type': 'WebPage',
    '@id': `${opts.url}#webpage`,
    url: opts.url,
    name: opts.name,
    description: opts.description,
    isPartOf: { '@id': WEBSITE_ID },
    ...(opts.image && {
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: opts.image.startsWith('http') ? opts.image : `${SITE.url}${opts.image}`,
      },
    }),
    ...(opts.aboutId && { about: { '@id': opts.aboutId } }),
    ...(opts.breadcrumbId && { breadcrumb: { '@id': opts.breadcrumbId } }),
    author: { '@id': personId(opts.authorSlug || defaultAuthor.slug) },
    publisher: { '@id': ORG_ID },
    datePublished: opts.datePublished ?? BUILD_DATE,
    dateModified: opts.dateModified ?? BUILD_DATE,
    inLanguage: lang,
  };
}

export function createPlaceSchema(name: string, description: string, lat: number, lng: number) {
  return {
    '@type': 'Place',
    name,
    description,
    geo: {
      '@type': 'GeoCoordinates',
      latitude: lat,
      longitude: lng,
    },
  };
}

export function createBreadcrumbSchema(items: { name: string; path: string }[], id?: string) {
  return {
    '@type': 'BreadcrumbList',
    ...(id && { '@id': id }),
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${SITE.url}${item.path}`,
    })),
  };
}

export function createFAQSchema(questions: { q: string; a: string }[]) {
  return {
    '@type': 'FAQPage',
    mainEntity: questions.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

export function createHowToSchema(
  name: string,
  description: string,
  totalTime: string,
  steps: { name: string; text: string; url?: string }[],
  estimatedCost?: { currency: string; value: string }
) {
  return {
    '@type': 'HowTo',
    name,
    description,
    totalTime,
    ...(estimatedCost && {
      estimatedCost: {
        '@type': 'MonetaryAmount',
        currency: estimatedCost.currency,
        value: estimatedCost.value,
      },
    }),
    step: steps.map(s => ({
      '@type': 'HowToStep',
      name: s.name,
      text: s.text,
      ...(s.url && { url: s.url }),
    })),
  };
}

export function createArticleSchema(
  opts: {
    headline: string;
    description: string;
    url: string;
    image: string;
    datePublished: string;
    dateModified?: string;
    author?: string;
    wordCount?: number;
  },
  lang: string = 'pt-BR'
) {
  return {
    '@type': 'Article',
    headline: opts.headline,
    description: opts.description,
    url: opts.url,
    image: opts.image,
    datePublished: opts.datePublished,
    dateModified: opts.dateModified ?? BUILD_DATE,
    author: opts.author
      ? { '@type': 'Organization', name: opts.author, url: SITE.url }
      : createOrganizationSchema(),
    publisher: createOrganizationSchema(),
    ...(opts.wordCount && { wordCount: opts.wordCount }),
    inLanguage: lang,
  };
}

export function createTravelGuideSchema(
  opts: {
    name: string;
    description: string;
    url: string;
    image: string;
    datePublished: string;
    dateModified?: string;
    about?: ReturnType<typeof createPlaceSchema>;
  },
  lang: string = 'pt-BR'
) {
  return {
    '@type': 'TravelGuide',
    name: opts.name,
    description: opts.description,
    url: opts.url,
    image: opts.image,
    datePublished: opts.datePublished,
    dateModified: opts.dateModified ?? BUILD_DATE,
    author: createOrganizationSchema(),
    publisher: createOrganizationSchema(),
    ...(opts.about && { about: opts.about }),
    inLanguage: lang,
  };
}

/* ─── Widget Schema — Product/Offer para afiliados ─── */

export interface WidgetAffiliateConfig {
  name: string;
  description: string;
  url: string;
  provider: string;
  price?: { min: number; max: number; currency: string };
  category?: string;
}

export function createWidgetProductSchema(config: WidgetAffiliateConfig): Record<string, unknown> | null {
  const hasPrice = config.price && config.price.currency && config.price.min != null && config.price.max != null;

  // Widgets sem preço (ex: previsão do tempo) não devem gerar schema Product/Offer
  if (!hasPrice) return null;

  return {
    '@type': 'Product',
    name: config.name,
    description: config.description,
    brand: {
      '@type': 'Brand',
      name: config.provider,
    },
    offers: {
      '@type': 'AggregateOffer',
      url: config.url,
      availability: 'https://schema.org/InStock',
      offerCount: 1,
      lowPrice: config.price!.min,
      highPrice: config.price!.max,
      priceCurrency: config.price!.currency,
    },
  };
}

/* ─── Page SEO Config Factory ─── */

export interface PageSEOConfig {
  title: string;
  description: string;
  keywords?: string;
  canonical: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export function createTouristDestinationSchema(opts: {
  name: string;
  description: string;
  url: string;
  image?: string;
  lat?: number;
  lng?: number;
  containedInPlace?: { name: string; addressCountry: string };
  containsPlace?: { name: string; description?: string; lat?: number; lng?: number }[];
  touristType?: string[];
}) {
  return {
    '@type': 'TouristDestination',
    name: opts.name,
    description: opts.description,
    url: opts.url,
    ...(opts.image && { image: opts.image }),
    ...(opts.lat !== undefined &&
      opts.lng !== undefined && {
        geo: {
          '@type': 'GeoCoordinates',
          latitude: opts.lat,
          longitude: opts.lng,
        },
      }),
    ...(opts.containedInPlace && {
      containedInPlace: {
        '@type': 'Place',
        name: opts.containedInPlace.name,
        address: {
          '@type': 'PostalAddress',
          addressCountry: opts.containedInPlace.addressCountry,
        },
      },
    }),
    ...(opts.containsPlace?.length && {
      containsPlace: opts.containsPlace.map(p => ({
        '@type': 'TouristAttraction',
        name: p.name,
        ...(p.description && { description: p.description }),
        ...(p.lat !== undefined &&
          p.lng !== undefined && {
            geo: {
              '@type': 'GeoCoordinates',
              latitude: p.lat,
              longitude: p.lng,
            },
          }),
      })),
    }),
    ...(opts.touristType?.length && { touristType: opts.touristType }),
  };
}

export function createTouristAttractionSchema(opts: {
  name: string;
  description: string;
  url: string;
  image?: string;
  lat?: number;
  lng?: number;
}) {
  return {
    '@type': 'TouristAttraction',
    name: opts.name,
    description: opts.description,
    url: opts.url,
    ...(opts.image && { image: opts.image }),
    ...(opts.lat !== undefined &&
      opts.lng !== undefined && {
        geo: {
          '@type': 'GeoCoordinates',
          latitude: opts.lat,
          longitude: opts.lng,
        },
      }),
  };
}

export function createItemListSchema(items: { name: string; url: string; description?: string }[]) {
  return {
    '@type': 'ItemList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      url: item.url,
      ...(item.description && { description: item.description }),
    })),
  };
}

/**
 * Triple JSON-LD Stack — GEO-ready
 * Combina Article + ItemList + FAQPage + TouristAttraction + HowTo
 * em um unico array de schemas.
 */
export function createTripleStackSchema(
  opts: {
    article: Parameters<typeof createArticleSchema>[0];
    faq?: { q: string; a: string }[];
    howTo?: ReturnType<typeof createHowToSchema>;
    touristAttraction?: ReturnType<typeof createTouristAttractionSchema>;
    itemList?: ReturnType<typeof createItemListSchema>;
    breadcrumb?: ReturnType<typeof createBreadcrumbSchema>;
  },
  lang: string = 'pt-BR'
) {
  const schemas: Record<string, unknown>[] = [];

  schemas.push(createArticleSchema(opts.article, lang));

  if (opts.breadcrumb) schemas.push(opts.breadcrumb);
  if (opts.touristAttraction) schemas.push(opts.touristAttraction);
  if (opts.itemList) schemas.push(opts.itemList);
  if (opts.howTo) schemas.push(opts.howTo);
  if (opts.faq?.length) schemas.push(createFAQSchema(opts.faq));

  return schemas;
}

export function createPageSEO(config: PageSEOConfig): PageSEOConfig {
  return config;
}

/* ─── Entity Schema Builder — determina schema.org por categoria ─── */

export type EntityCategory =
  | 'praia' | 'trilha' | 'atracao' | 'passeio'
  | 'pousada' | 'restaurante' | 'morro' | 'vila'
  | 'ilha' | 'trapiche';

export function createEntitySchema(
  entity: Record<string, any>,
  category: EntityCategory,
  canonicalUrl: string,
  lang: string = 'pt-BR'
): Record<string, unknown> {
  // dentro de um @graph: usa @id e liga à WebPage (mainEntityOfPage)
  const base = {
    '@type': 'TouristAttraction',
    '@id': `${canonicalUrl}#place`,
    name: entity.title,
    description: entity.description?.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1').substring(0, 300),
    url: canonicalUrl,
    image: entity.image?.startsWith('http')
      ? entity.image
      : `${SITE.url}${entity.image}`,
    mainEntityOfPage: { '@id': `${canonicalUrl}#webpage` },
    isPartOf: { '@id': WEBSITE_ID },
    inLanguage: lang,
  };

  switch (category) {
    case 'pousada': {
      const reviews = [] as any[];
      if (entity.reviews?.length) {
        entity.reviews.forEach((r: any) => {
          reviews.push({
            '@type': 'Review',
            author: { '@type': 'Person', name: r.author },
            reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5 },
            reviewBody: r.text,
          });
        });
      }
      // aggregateRating só com nota E contagem REAIS na mesma plataforma; bestRating correto por escala
      // (Booking = 0-10, TripAdvisor/Google = 0-5). Evita rich snippet inválido/falso.
      let agg: Record<string, unknown> | null = null;
      if (entity.rating?.tripAdvisor && entity.reviewCount?.tripAdvisor)
        agg = { ratingValue: entity.rating.tripAdvisor, bestRating: 5, reviewCount: entity.reviewCount.tripAdvisor };
      else if (entity.rating?.booking && entity.reviewCount?.booking)
        agg = { ratingValue: entity.rating.booking, bestRating: 10, reviewCount: entity.reviewCount.booking };
      else if (entity.rating?.google && entity.reviewCount?.google)
        agg = { ratingValue: entity.rating.google, bestRating: 5, reviewCount: entity.reviewCount.google };
      const AMENITY_LABEL: Record<string, string> = {
        'wi-fi': 'Wi-Fi', 'cafe-da-manha': 'Café da manhã', 'ar-condicionado': 'Ar-condicionado',
        restaurante: 'Restaurante', 'estacionamento-pontal': 'Estacionamento em Pontal do Sul',
        varanda: 'Varanda', 'cozinha-compartilhada': 'Cozinha compartilhada', 'pet-friendly': 'Aceita animais',
        piscina: 'Piscina', churrasqueira: 'Churrasqueira', bar: 'Bar', 'vista-mar': 'Vista para o mar',
        'pe-na-areia': 'Pé na areia', 'musica-ao-vivo': 'Música ao vivo',
      };
      const parseHora = (s?: string) => {
        const m = s && /(\d{1,2})\s*h/.exec(s);
        return m ? `${m[1].padStart(2, '0')}:00` : null;
      };
      const checkinTime = parseHora(entity.politicas?.checkIn);
      const checkoutTime = parseHora(entity.politicas?.checkOut);
      return {
        ...base,
        '@type': 'LodgingBusiness',
        ...(entity.location?.address && { address: {
          '@type': 'PostalAddress',
          addressLocality: entity.vilarejoName || 'Ilha do Mel',
          addressRegion: 'PR',
          addressCountry: 'BR',
          streetAddress: entity.location.address,
        }}),
        ...(entity.schemaOrg?.geo && { geo: {
          '@type': 'GeoCoordinates',
          latitude: entity.schemaOrg.geo.latitude,
          longitude: entity.schemaOrg.geo.longitude,
        }}),
        ...(entity.amenities?.length && { amenityFeature: entity.amenities.map((a: string) => ({
          '@type': 'LocationFeatureSpecification',
          name: AMENITY_LABEL[a] || a.replace(/-/g, ' '),
          value: true,
        }))}),
        ...(checkinTime && { checkinTime }),
        ...(checkoutTime && { checkoutTime }),
        ...(entity.politicas?.pets && { petsAllowed: !/n[ãa]o/i.test(entity.politicas.pets) }),
        ...(agg && { aggregateRating: { '@type': 'AggregateRating', worstRating: 1, ...agg } }),
        ...(reviews.length && { review: reviews }),
        ...(entity.priceRange?.min && entity.priceRange?.max && {
          priceRange: `R$${entity.priceRange.min}-R$${entity.priceRange.max}`,
        }),
      };
    }

    case 'restaurante': {
      // Super página: rating é objeto {tripAdvisor,google,booking}; faixaPreco é string ($/$$/$$$)
      const r = entity.rating || {};
      const rc = entity.reviewCount || {};
      const agg =
        r.tripAdvisor && rc.tripAdvisor
          ? { ratingValue: r.tripAdvisor, ratingCount: rc.tripAdvisor, bestRating: 5 }
          : r.google && rc.google
            ? { ratingValue: r.google, ratingCount: rc.google, bestRating: 5 }
            : typeof r === 'number' && entity.reviewCount // compat formato antigo
              ? { ratingValue: r, ratingCount: entity.reviewCount, bestRating: 5 }
              : null;
      const reviews = (entity.reviews || [])
        .filter((rv: any) => rv.text && rv.author)
        .slice(0, 5)
        .map((rv: any) => ({
          '@type': 'Review',
          author: { '@type': 'Person', name: rv.author },
          ...(rv.rating && {
            reviewRating: { '@type': 'Rating', ratingValue: rv.rating > 5 ? rv.rating / 2 : rv.rating, bestRating: 5 },
          }),
          reviewBody: rv.text,
        }));
      return {
        ...base,
        '@type': 'Restaurant',
        address: {
          '@type': 'PostalAddress',
          addressLocality: entity.vilarejoName || 'Ilha do Mel',
          addressRegion: 'PR',
          addressCountry: 'BR',
          ...(entity.location?.address && { streetAddress: entity.location.address }),
        },
        ...(entity.cuisine?.length && { servesCuisine: entity.cuisine }),
        ...(entity.faixaPreco
          ? { priceRange: entity.faixaPreco }
          : entity.priceRange && {
              priceRange: entity.priceRange.indicator || `R$${entity.priceRange.min}-R$${entity.priceRange.max}`,
            }),
        ...(entity.reservaRecomendada != null && { acceptsReservations: !!entity.reservaRecomendada }),
        ...(agg && { aggregateRating: { '@type': 'AggregateRating', worstRating: 1, ...agg } }),
        ...(reviews.length && { review: reviews }),
      };
    }

    case 'passeio': {
      const offer = entity.price?.amount
        ? {
            '@type': 'AggregateOffer',
            url: canonicalUrl,
            offerCount: 1,
            lowPrice: entity.price.amount,
            highPrice: entity.price.amount,
            priceCurrency: entity.price.currency || 'BRL',
            availability: 'https://schema.org/InStock',
            ...(entity.price.notes && { description: entity.price.notes }),
          }
        : undefined;

      const reviews = [] as any[];
      if (entity.testimonials?.length) {
        entity.testimonials.forEach((r: any) => {
          reviews.push({
            '@type': 'Review',
            author: { '@type': 'Person', name: r.name },
            reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5 },
            reviewBody: r.text,
          });
        });
      }

      const aggRating = entity.rating;
      const aggCount = entity.reviewCount;

      return {
        ...base,
        '@type': 'Product',
        brand: entity.provider?.name
          ? { '@type': 'Brand', name: entity.provider.name }
          : undefined,
        ...(offer && { offers: offer }),
        ...(aggRating && aggCount > 0 && {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: aggRating,
            reviewCount: aggCount,
            bestRating: 5,
          },
        }),
        ...(reviews.length && { review: reviews }),
      };
    }

    case 'praia': {
      const aggRating = entity.rating;
      const aggCount = entity.reviewCount;
      return {
        ...base,
        '@type': ['TouristAttraction', 'Beach'],
        address: {
          '@type': 'PostalAddress',
          addressLocality: entity.vilarejoName
            ? `${entity.vilarejoName}, Ilha do Mel`
            : 'Ilha do Mel',
          addressRegion: 'PR',
          addressCountry: 'BR',
        },
        ...(entity.schemaOrg?.geo && {
          geo: {
            '@type': 'GeoCoordinates',
            latitude: entity.schemaOrg.geo.latitude,
            longitude: entity.schemaOrg.geo.longitude,
          },
        }),
        ...(entity.facilities?.length && { amenityFeature: entity.facilities.map((f: string) => ({
          '@type': 'LocationFeatureSpecification',
          name: f,
          value: true,
        }))}),
        ...(typeof aggRating === 'number' && aggRating > 0 && {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: aggRating,
            ...(aggCount && { reviewCount: aggCount }),
            bestRating: 5,
            worstRating: 1,
          },
        }),
      };
    }

    default:
      return base;
  }
}
